/**
 * Период учёта — пара (year, month), month = 1..12, и день начала цикла.
 *
 * Период всегда остаётся парой (year, month): меняется только то, какие даты в
 * него попадают. При cycleStartDay = 1 период — календарный месяц (как раньше),
 * при 25 — окно с 25-го числа месяца по 24-е следующего. Период называется по
 * месяцу начала: цикл 25.01–24.02 — это «январь».
 */
/** День начала цикла: 1 — обычный календарный месяц. */
export const DEFAULT_CYCLE_START_DAY = 1;
export const MIN_CYCLE_START_DAY = 1;
export const MAX_CYCLE_START_DAY = 31;
/** День начала цикла из настроек: всё вне 1..31 сводится к календарному месяцу. */
export function clampCycleStartDay(value) {
    if (!Number.isFinite(value))
        return DEFAULT_CYCLE_START_DAY;
    const day = Math.trunc(value);
    if (day < MIN_CYCLE_START_DAY)
        return MIN_CYCLE_START_DAY;
    if (day > MAX_CYCLE_START_DAY)
        return MAX_CYCLE_START_DAY;
    return day;
}
const PERIOD_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;
export function parsePeriod(value) {
    const m = PERIOD_RE.exec(value.trim());
    if (!m)
        throw new Error(`parsePeriod: expected YYYY-MM, got "${value}"`);
    return { year: Number(m[1]), month: Number(m[2]) };
}
export function formatPeriod(p) {
    return `${String(p.year).padStart(4, '0')}-${String(p.month).padStart(2, '0')}`;
}
export function shiftPeriod(p, delta) {
    const zero = p.year * 12 + (p.month - 1) + delta;
    return { year: Math.floor(zero / 12), month: (zero % 12) + 1 };
}
/** Сравнение периодов: <0 — a раньше b, 0 — тот же период, >0 — позже. */
export function comparePeriods(a, b) {
    return a.year * 12 + a.month - (b.year * 12 + b.month);
}
export function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/** billing_day 29..31 в коротком месяце схлопывается к последнему дню. */
export function clampDayToMonth(day, year, month) {
    const last = daysInMonth(year, month);
    return Math.min(Math.max(day, 1), last);
}
/**
 * Дата календарного дня `day` внутри КАЛЕНДАРНОГО месяца периода.
 * Для дней внутри цикла (платежи, подписки) используйте dayOfMonthInCycle.
 */
export function dayToDate(day, p) {
    const d = clampDayToMonth(day, p.year, p.month);
    return `${String(p.year).padStart(4, '0')}-${String(p.month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
/** Сдвиг ISO-даты на дни. Считаем в UTC: переходы на летнее время нам не мешают. */
export function shiftIsoDate(isoDate, days) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
    if (!m)
        throw new Error(`shiftIsoDate: expected YYYY-MM-DD, got "${isoDate}"`);
    const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
    const pad = (value) => String(value).padStart(2, '0');
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}
/** Разница в целых днях между двумя ISO-датами (b − a). */
export function daysBetweenIso(a, b) {
    const toUtc = (isoDate) => {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
        if (!m)
            throw new Error(`daysBetweenIso: expected YYYY-MM-DD, got "${isoDate}"`);
        return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    };
    return Math.round((toUtc(b) - toUtc(a)) / 86_400_000);
}
/** Текущая дата в заданной IANA-зоне как YYYY-MM-DD (по умолчанию Europe/Minsk). */
export function todayInTz(timeZone = 'Europe/Minsk', now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
    return parts;
}
/**
 * Период, которому принадлежит дата, при заданном дне начала цикла.
 *
 * День >= начала цикла попадает в месяц даты, иначе — в предыдущий: 3 февраля
 * при начале 25-го — это ещё январский цикл. Начало берём со схлопыванием
 * (clampDayToMonth), поэтому 28 февраля при cycleStartDay = 31 — уже февраль.
 */
export function periodOfDate(isoDate, cycleStartDay = DEFAULT_CYCLE_START_DAY) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
    if (!m)
        throw new Error(`periodOfDate: expected YYYY-MM-DD, got "${isoDate}"`);
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const start = clampDayToMonth(clampCycleStartDay(cycleStartDay), year, month);
    return day >= start ? { year, month } : shiftPeriod({ year, month }, -1);
}
export function currentPeriod(timeZone = 'Europe/Minsk', now = new Date(), cycleStartDay = DEFAULT_CYCLE_START_DAY) {
    return periodOfDate(todayInTz(timeZone, now), cycleStartDay);
}
/** Первый день цикла (year, month) как YYYY-MM-DD. */
export function cycleStartDate(p, cycleStartDay = DEFAULT_CYCLE_START_DAY) {
    return dayToDate(clampCycleStartDay(cycleStartDay), p);
}
/** Последний день цикла — день перед началом следующего. */
export function cycleEndDate(p, cycleStartDay = DEFAULT_CYCLE_START_DAY) {
    return shiftIsoDate(cycleStartDate(shiftPeriod(p, 1), cycleStartDay), -1);
}
/**
 * Дата календарного дня `day` (1..31) внутри цикла.
 *
 * Цикл тянется от своего начала до начала следующего и захватывает хвост месяца
 * периода и голову следующего. Сначала ищем в цикле настоящее число `day`: в
 * цикле 25.01–24.02 платёж 1-го числа — это 1 февраля, а не 1 января; в цикле
 * 28.02–30.03 (начало 31-го, февраль схлопнут к 28-му) платёж 30-го — это
 * 30 марта, а не 28 февраля. Если число в цикл не попадает (в феврале нет 30-го,
 * а 30 марта уже в следующем цикле), схлопываем, не выходя за границы цикла:
 * день не раньше начала цикла — к последнему дню месяца периода, иначе — к
 * последнему дню цикла. Так в каждом цикле ровно одна дата.
 */
export function dayOfMonthInCycle(day, p, cycleStartDay = DEFAULT_CYCLE_START_DAY) {
    const start = cycleStartDate(p, cycleStartDay);
    const end = cycleEndDate(p, cycleStartDay);
    const next = shiftPeriod(p, 1);
    for (const q of [p, next]) {
        if (day > daysInMonth(q.year, q.month))
            continue;
        const date = dayToDate(day, q);
        if (date >= start && date <= end)
            return date;
    }
    const startDay = clampDayToMonth(clampCycleStartDay(cycleStartDay), p.year, p.month);
    if (day >= startDay)
        return dayToDate(day, p);
    const clamped = dayToDate(day, next);
    return clamped <= end ? clamped : end;
}
//# sourceMappingURL=period.js.map